import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

import { defaultGenericMeta } from "store/common";
import { AccountInfo, AccountState } from "./account-state";
import { fetchAccounts, fetchNonValidatedAccount } from "./account-action";

export const accountEntityAdapter = createEntityAdapter<AccountInfo>();

export const accountSlice = createSlice({
  name: "account",
  initialState: accountEntityAdapter.getInitialState<AccountState>({
    meta: { ...defaultGenericMeta },
  }),
  reducers: {
    changeAccount(state, { payload: id }: PayloadAction<number>) {
      state.currentSelectedAccount = id;
    },
    resetAccounts(state) {
      accountEntityAdapter.removeAll(state);
      delete state.currentSelectedAccount;
      state.meta = { ...defaultGenericMeta };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchAccounts.fulfilled,
      (state, { payload }: PayloadAction<AccountInfo[]>) => {
        payload.forEach((account) => {
          const index = account.dueDate.indexOf("Z");
          if (index >= 0) {
            account.dueDate = account.dueDate.substring(0, index);
          }
        });

        accountEntityAdapter.setAll(state, payload);

        // AccountIds array for below check
        const accountIds = payload.map((item) => item.id);

        // Find first active account, or fall back to first account overall
        const firstActiveAccount = payload.find(
          (account) => account.active === true,
        );
        const fallbackAccount = firstActiveAccount || payload[0];

        // 43240 This allows for currentSelectedAccount to persist but has to be selected correctly
        // In the logic below if currentSelectedAccount is null then it defaults to the first active account (or first account if none active)
        // Otherwise another check is done to determine if the currentSelectedAccount is in the returned accountIds (if it is use current state otherwise use first active/first account)
        // This was added for multiple users on one client system, otherwise only one user could be used on one client.
        state.currentSelectedAccount =
          state.currentSelectedAccount == null
            ? fallbackAccount?.id
            : accountIds.includes(state.currentSelectedAccount)
              ? state.currentSelectedAccount
              : fallbackAccount?.id;
        state.meta.isLoading = false;
        state.meta.isSuccess = true;
        state.meta.status = "idle";
        state.meta.error = null;
      },
    );
    builder.addCase(fetchAccounts.pending, (state) => {
      state.meta.isLoading = true;
      state.meta.status = "inProcess";
    });
    builder.addCase(fetchAccounts.rejected, (state) => {
      state.meta.isLoading = false;
      state.meta.status = "idle";
      state.meta.error = "true";
    });
    builder.addCase(
      fetchNonValidatedAccount.fulfilled,
      (state, { payload }: PayloadAction<AccountInfo[]>) => {
        payload.forEach((account) => {
          const index = account.dueDate.indexOf("Z");
          if (index >= 0) {
            account.dueDate = account.dueDate.substring(0, index);
          }
        });

        accountEntityAdapter.setAll(state, payload);
        state.currentSelectedAccount = payload[0]?.id;
        state.meta.isLoading = false;
        state.meta.isSuccess = true;
        state.meta.status = "idle";
        state.meta.error = null;
      },
    );
    builder.addCase(fetchNonValidatedAccount.pending, (state) => {
      state.meta.isLoading = true;
      state.meta.status = "inProcess";
    });
    builder.addCase(fetchNonValidatedAccount.rejected, (state) => {
      state.meta.isLoading = false;
      state.meta.status = "idle";
      state.meta.error = "true";
    });
  },
});

export const { changeAccount, resetAccounts } = accountSlice.actions;
export default accountSlice.reducer;
